import { calculateOrderTotal } from "@lemmonhq/orders";
import {
  Order,
  OrderItem,
  PaymentDetails,
  SuccesfullPartialPayment,
  TableFullCheckout,
  TablePartialCheckout,
  TableSplitCheckout,
} from "@lemmonhq/types";
import { getOrderItemGroupId } from "@app/utils/clientBusiness";

export const getOrderItemsGrouped = (orders: Order[]) => {
  const allItems = orders.flatMap((order) => order.items || []);
  const groups = new Map<
    string,
    { items: OrderItem[]; totalQuantity: number }
  >();

  allItems.forEach((item) => {
    const groupId = getOrderItemGroupId(item);
    if (!groups.has(groupId)) {
      groups.set(groupId, { items: [], totalQuantity: 0 });
    }
    const group = groups.get(groupId)!;
    group.items.push(item);
    group.totalQuantity += item.quantity;
  });

  return Array.from(groups.entries()).map(([groupId, group]) => ({
    groupId,
    items: group.items,
    totalQuantity: group.totalQuantity,
    firstItem: group.items[0],
  }));
};

interface ItemPaymentStatus {
  paidQuantity: number;
  remainingQuantity: number;
  totalPrice: number;
  remainingPrice: number;
  status?: "paid" | "cancelled";
}

/**
 * Calculates the total price for an order item including any bundles
 */
export const calculateItemPrice = (item: OrderItem): number => {
  const basePrice = item.price;
  const bundlePrice =
    item.bundles?.reduce((total, bundle) => total + bundle.price, 0) || 0;
  return basePrice + bundlePrice;
};

/**
 * Gets the payment status for a specific order item
 */
export const getItemPaymentStatus = (
  item: OrderItem,
  partialPayments: SuccesfullPartialPayment[],
): ItemPaymentStatus => {
  const itemPrice = calculateItemPrice(item);
  const paidQuantity = partialPayments
    .filter((payment) => payment.order_item_id === item.id)
    .reduce((total, payment) => total + payment.quantity, 0);

  const remainingQuantity = Math.max(0, item.quantity - paidQuantity);

  const totalPrice = itemPrice * item.quantity;
  const remainingPrice = itemPrice * remainingQuantity;
  return {
    paidQuantity,
    remainingQuantity,
    totalPrice,
    remainingPrice,
    status:
      item.quantity === 0
        ? "cancelled"
        : remainingQuantity === 0
          ? "paid"
          : undefined,
  };
};

export const getRemainingUnpaidItemQuantities = (
  orders: Order[],
  partialPayments: SuccesfullPartialPayment[],
): Record<number, number> => {
  const remainingItems: Record<number, number> = {};
  const allItems = orders.flatMap((order) => order.items || []);

  allItems.forEach((item) => {
    const { remainingQuantity } = getItemPaymentStatus(item, partialPayments);
    if (remainingQuantity > 0) {
      remainingItems[item.id] = remainingQuantity;
    }
  });

  return remainingItems;
};

export const calculatePaidTotal = (payments: PaymentDetails[]) => {
  // subtotal field was added later so if absent we need to compute it backwards if undefined
  const paidAmounts = payments.reduce(
    (total, payment) =>
      total +
      (payment.subtotal !== undefined && Number.isFinite(payment.subtotal)
        ? payment.subtotal
        : payment.amount + (payment.discount ? payment.discount.net : 0)),
    0,
  );
  return paidAmounts;
};

/**
 * Calculates the total remaining amount to be paid for all orders
 */
export const calculateRemainingTotal = (
  orders: Order[],
  payments: PaymentDetails[],
): number => {
  const paidAmounts = calculatePaidTotal(payments);

  const orderTotal = orders.reduce(
    (total, order) => total + calculateOrderTotal(order),
    0,
  );

  return orderTotal - paidAmounts;
};

/**
 * Creates a payload for partial payment with selected items and quantities
 */

export const getSelectedItemsTotals = (
  orders: Order[],
  selectedItems: Record<number, number>,
) => {};
export const createPartialPaymentPayload = (
  orders: Order[],
  selectedItems: Record<number, number>,
  tableId: number,
  partialPayments: SuccesfullPartialPayment[] = [],
): TablePartialCheckout => {
  const paymentItems = Object.entries(selectedItems).map(
    ([itemId, quantity]) => {
      const item = orders
        .flatMap((order) => order.items || [])
        .find((item) => item.id === Number(itemId));

      if (!item) {
        throw new Error(`Item with id ${itemId} not found`);
      }

      return {
        id: item.id,
        quantity,
        productId: item.product_id,
        orderId: item.order_id,
        paidQuantity: getItemPaymentStatus(item, partialPayments).paidQuantity,
        total: calculateItemPrice(item) * quantity,
      };
    },
  );
  const orderIds = new Set<number>(paymentItems.map((el) => el.orderId));

  return {
    type: "partial",
    table_id: tableId,
    tableOrders: orders,
    close: Array.from(orderIds),
    orders,
    items: paymentItems,
  };
};

/**
 * Creates a payload for full payment of all remaining unpaid items
 */
export const createFullPaymentPayload = (
  orders: Order[],
  partialPayments: SuccesfullPartialPayment[],
  tableId: number,
): TableFullCheckout => {
  return {
    type: "full",
    table_id: tableId,
    tableOrders: orders,
    close: orders.map((el) => el.id),
    orders,
  };
};

export const createSplitPaymentPayload = (
  orders: Order[],
  partialPayments: SuccesfullPartialPayment[],
  tableId: number,
  splitAmount: number,
  splitSequence: number,
  splitType: string,
): TableSplitCheckout => {
  return {
    type: "split",
    table_id: tableId,
    tableOrders: orders,
    close: orders.map((el) => el.id),
    orders,
    splitAmount,
    splitSequence,
    splitType,
  };
};
